import DsoTools from '@/lib/es-bill-shared-js/misc/DsoTools';
import { applyGroupDisplays } from '@/services/billrunEnergyDataExportsService';
import { getFilteredIssueResults } from '@/services/billrunEnergyDataInterpretationService';
import { formatDateRangesForDisplay, normalizeToLocalMidnight } from '@/services/billrunFiltersService';

const PROBLEM_ORDER = ['l3', 'incorrectQh', 'missingData', 'surplus'];
const PROBLEM_I18N_KEYS = {
  l3: 'problemTypeL3',
  incorrectQh: 'problemTypeIncorrectQh',
  missingData: 'problemTypeMissingData',
  surplus: 'problemTypeSurplus',
};

function uniqueMeteringPointCount(rows) {
  return new Set(rows.map((row) => `${row.ecid}:${row.meteringPoint}`)).size;
}

function combinedDateRanges(rows) {
  const dates = rows.flatMap((row) => row.issueDates ?? []);
  const uniqueDates = [...new Map(
    dates
      .map(validDate)
      .filter(Boolean)
      .map((date) => [date.getTime(), date])
  ).values()];
  return formatDateRangesForDisplay(uniqueDates);
}

/**
 * Builds the analysis-page summary from the rendered result rows. Counts are
 * based on unique community/metering-point pairs; a metering point with more
 * than one problem therefore counts once in the total and once per category.
 */
export function buildPeriodAnalysisSummary(rows) {
  const dsoGroups = new Map();
  const problemGroups = new Map();

  for (const row of rows) {
    const dsoKey = row.ecnDso || row.dsoDisplayFull || '';
    if (!dsoGroups.has(dsoKey)) dsoGroups.set(dsoKey, []);
    dsoGroups.get(dsoKey).push(row);

    const problemKey = row.problemType || '';
    if (!problemGroups.has(problemKey)) problemGroups.set(problemKey, []);
    problemGroups.get(problemKey).push(row);
  }

  const toSummaryRow = ([key, groupedRows]) => ({
    key,
    meteringPointCount: uniqueMeteringPointCount(groupedRows),
    dateRanges: combinedDateRanges(groupedRows),
  });

  const byProblemType = [...problemGroups.entries()]
    .map(([key, groupedRows]) => ({
      ...toSummaryRow([key, groupedRows]),
      problemType: key,
      problemTypeI18nKey: groupedRows[0]?.problemTypeI18nKey,
    }))
    .sort((a, b) => PROBLEM_ORDER.indexOf(a.problemType) - PROBLEM_ORDER.indexOf(b.problemType));

  const byDso = [...dsoGroups.entries()]
    .map(([key, groupedRows]) => ({
      ...toSummaryRow([key, groupedRows]),
      dso: groupedRows[0]?.dsoDisplayFull || key,
    }))
    .sort((a, b) => a.dso.localeCompare(b.dso, undefined, { numeric: true }));

  return {
    dsoCount: dsoGroups.size,
    meteringPointCount: uniqueMeteringPointCount(rows),
    dateRanges: combinedDateRanges(rows),
    byProblemType,
    byDso,
  };
}

function validDate(value) {
  if (!value) return null;
  const date = normalizeToLocalMidnight(new Date(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function displayDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

function dsoDetails(community, meteringPoint) {
  const fallback = community.ecnDso || String(meteringPoint).substring(0, 8);
  try {
    const detected = DsoTools.detectDso(community, String(meteringPoint).substring(0, 8));
    return { ecnDso: detected, label: `${detected} - ${DsoTools.getDsoByEcn(detected).title}` };
  } catch {
    try {
      return { ecnDso: fallback, label: `${fallback} - ${DsoTools.getDsoByEcn(fallback).title}` };
    } catch {
      return { ecnDso: fallback, label: fallback };
    }
  }
}

function addIssue(buckets, context, entry, problemType, codes = []) {
  const meteringPoint = entry?.meteringPoint;
  if (!meteringPoint) return;
  const key = `${context.community.ecid}:${meteringPoint}:${problemType}`;
  if (!buckets.has(key)) {
    buckets.set(key, { context, meteringPoint, problemType, codes: new Set(), dates: [], updates: [] });
  }
  const bucket = buckets.get(key);
  codes.filter(Boolean).forEach((code) => bucket.codes.add(code));
  const date = validDate(entry.date);
  if (date) bucket.dates.push(date);
  if (entry.dateLastUpdate) bucket.updates.push(new Date(entry.dateLastUpdate));
}

export function buildPeriodAnalysisRows(results, { billingRelevantOnly = false } = {}) {
  const buckets = new Map();
  for (const context of results) {
    const filtered = getFilteredIssueResults({
      uncleanMeteringPointsDetailResults: context.unclean,
      incorrectCountOfQhResults: context.incorrectQh,
      incorrectMeterCodesResults: context.incorrectCodes,
    }, { billingRelevantOnly });

    for (const entry of filtered.uncleanMeteringPointsDetailResults) {
      addIssue(buckets, context, entry, 'l3', [entry.meterCode]);
    }
    for (const entry of filtered.incorrectCountOfQhResults) {
      addIssue(buckets, context, entry, 'incorrectQh', [entry.meterCode]);
    }
    for (const entry of filtered.incorrectMeterCodesResults) {
      if (entry.meterCodesMissing?.length) {
        addIssue(buckets, context, entry, 'missingData', entry.meterCodesMissing);
      }
      if (entry.meterCodesSurplus?.length) {
        addIssue(buckets, context, entry, 'surplus', entry.meterCodesSurplus);
      }
    }
  }

  const rows = [...buckets.values()].map((bucket) => {
    const community = bucket.context.community;
    const dso = dsoDetails(community, bucket.meteringPoint);
    const latestUpdate = bucket.updates.sort((a, b) => b - a)[0];
    const issueDates = [...new Map(bucket.dates.map((d) => [d.getTime(), d])).values()];
    const codePrefix = bucket.problemType === 'missingData' ? 'missing: ' : bucket.problemType === 'surplus' ? 'surplus: ' : '';
    return {
      ecid: community.ecid,
      ecn: community.ecn,
      ecnDso: dso.ecnDso,
      dsoDisplayFull: dso.label,
      communityDisplayFull: `${community.ecn} - ${community.name}`,
      meteringPoint: bucket.meteringPoint,
      problemType: bucket.problemType,
      problemTypeI18nKey: PROBLEM_I18N_KEYS[bucket.problemType],
      meterCodes: `${codePrefix}${[...bucket.codes].sort().join(', ')}`,
      lastUpdate: displayDate(latestUpdate),
      dateRanges: formatDateRangesForDisplay(issueDates),
      issueDates,
      dsoDisplay: '',
      communityDisplay: '',
    };
  });

  rows.sort((a, b) =>
    a.ecnDso.localeCompare(b.ecnDso, undefined, { numeric: true })
    || a.ecn.localeCompare(b.ecn, undefined, { numeric: true })
    || a.meteringPoint.localeCompare(b.meteringPoint)
    || PROBLEM_ORDER.indexOf(a.problemType) - PROBLEM_ORDER.indexOf(b.problemType)
  );
  return applyGroupDisplays(rows);
}
